import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  PageHeader,
  Popconfirm,
  Tabs,
} from "antd";
import { StopOutlined } from "@ant-design/icons";
import LayoutCss from "layout/layout.module.scss";
import Candidate from "redux/models/candidate";
import UserDetailCard from "common/userDetailCard/userDetailCard";
import ViewActivity from "features/candidates/viewActivity";
import { isPermissionPresent } from "utilities/generalUtility";
import K from "utilities/constants";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "redux/slices/userSlice";
import { setShouldReinitialize } from "redux/slices/candidateSlice";
import styles1 from "../clients/create/clientsJob.module.scss";
import styles from "./candidatesDetails.module.scss";
import { ActivityTab } from "./activityTab";
import { CandidateJobEntryCard } from "./jobCard";
import { ParseNumber, useQueryState } from "../../hooks/useQueryState";
import { useQueryParams } from "../../hooks/useQueryParams";

const { TextArea } = Input;

const mergeCandidateInJobEntry = (candidate, jobEntry) => {
  return {
    ...jobEntry,
    candidate: {
      ...jobEntry.candidate,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      currentTitle: candidate.currentTitle,
    },
  };
};

export default function CandidatesDetails() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { setParams } = useQueryParams();
  const history = useHistory();
  const [reasonForm] = Form.useForm();
  const [candidateDetail, setCandidateDetail] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [candidateActivity, setCandidateActivity] = useState(null);
  // The nested `jobSource` of a candidate job entry does not carry the job
  // recruiters, so they are fetched separately for the permission checks. The
  // job they belong to is kept alongside them: on switching entries the state
  // still holds the previous job's answer, and rendering tabs with it would
  // apply the permissions of the job the user just left.
  const [fetchedJobRecruiters, setFetchedJobRecruiters] = useState(null);
  const userSlice = useSelector(selectUser);

  const [tab, setTab] = useQueryState("tab", "jobs");
  const [jobEntry, setJobEntry] = useQueryState("jobEntry", null, ParseNumber);
  const [candidateTab, setCandidateTab] = useQueryState(
    "candidateTab",
    "details",
  );

  const selectedJobEntry = candidateDetail?.candidateJobEntries?.find(
    (e) => e.id === jobEntry,
  );

  const handleCancelReasonModal = () => {
    setIsModalVisible(false);
    reasonForm.resetFields();
  };

  const handleOkReasonModal = () => {
    reasonForm.submit();
    setIsModalVisible(false);
  };

  const getCandidateDetailById = async (candidateId) => {
    try {
      const res = await Candidate.getCandidateDetail(candidateId);
      const { firstName, lastName, phoneNumber: phone, ...rest } = res;
      setCandidateDetail({
        ...rest,
        firstName,
        lastName,
        phone,
        name: [firstName, lastName].join(" "),
      });
      reasonForm.setFieldValue("id", candidateId);
    } catch (error) {
      message.error(error.message);
    }
  };

  const addToBlacklist = async (data) => {
    try {
      const res = await Candidate.addToBlacklist(data);
      setCandidateDetail((prev) => ({
        ...prev,
        isBlacklist: res.isBlacklist,
        blacklistReason: res.blacklistReason,
        id: res.id,
      }));
      reasonForm.resetFields();
    } catch (error) {
      reasonForm.resetFields();
      console.error("error", error);
    }
  };

  const removeToBlacklist = async () => {
    try {
      const res = await Candidate.removeToBlacklist({
        id,
      });
      setCandidateDetail((prev) => ({ ...prev, isBlacklist: res.isBlacklist }));
      reasonForm.resetFields();
    } catch (error) {
      console.error("error", error);
    }
  };

  const fetchCandidateDetails = async (jobEntryId) => {
    try {
      const res = await Candidate.getActivity(jobEntryId);

      setCandidateActivity(res);
      dispatch(setShouldReinitialize());
    } catch (err) {
      message.error("Failed to fetch candidate!");
      console.error(err);
    }
  };

  const getExtraPageHeader = () => {
    if (
      isPermissionPresent(
        K.Permissions.ManageCandidateBlacklist,
        userSlice.roles,
      )
    ) {
      return candidateDetail.isBlacklist === 0 ? (
        <Button
          type="ghost"
          icon={<StopOutlined />}
          onClick={() => setIsModalVisible(true)}
        >
          Add to Blacklist
        </Button>
      ) : (
        <Popconfirm
          title="Are you sure ?"
          onConfirm={removeToBlacklist}
          okText="Yes"
          cancelText="No"
        >
          <Button type="ghost" icon={<StopOutlined />}>
            Remove from Blacklist
          </Button>
        </Popconfirm>
      );
    }

    return null;
  };

  const updateCandidateJobEntry = (newEntry) => {
    setCandidateDetail((prev) => ({
      ...prev,
      candidateJobEntries: prev.candidateJobEntries.map((entry) => {
        if (entry.id === newEntry.id) {
          return {
            ...entry,
            status: newEntry.status,
            subStatus: newEntry.subStatus,
            otherStatus: newEntry.otherStatus,
          };
        }
        return entry;
      }),
    }));
  };

  useEffect(() => {
    if (selectedJobEntry) {
      fetchCandidateDetails(selectedJobEntry.id);
    }
  }, [selectedJobEntry]);

  useEffect(() => {
    getCandidateDetailById(id);
  }, [id]);

  useEffect(() => {
    const jobSourceId = candidateActivity?.jobSource?.id;

    if (!jobSourceId) {
      return undefined;
    }

    let isCancelled = false;

    // This only decides whether a tab is shown, so a failure must not take the
    // page down: a 403 means "not allowed", not "redirect to /unauthorized".
    Candidate.getJobRecruiterByJobId(jobSourceId, false, {
      showError: false,
      skipForbiddenRedirect: true,
    })
      .then((items) => {
        if (!isCancelled) setFetchedJobRecruiters({ jobSourceId, items });
      })
      .catch((err) => {
        if (!isCancelled) setFetchedJobRecruiters({ jobSourceId, items: [] });
        console.error(err);
      });

    return () => {
      isCancelled = true;
    };
  }, [candidateActivity?.jobSource?.id]);

  const jobRecruiters =
    fetchedJobRecruiters &&
    fetchedJobRecruiters.jobSourceId === candidateActivity?.jobSource?.id
      ? fetchedJobRecruiters.items
      : null;

  useEffect(() => {
    window.history.replaceState({}, "");
  }, []);

  return (
    candidateDetail && (
      <>
        <PageHeader
          ghost={false}
          className={LayoutCss.appPageHeader}
          onBack={history.goBack}
          title={candidateDetail.name}
          extra={getExtraPageHeader()}
        />
        <div className={styles.candidateDetailsWrapper}>
          <UserDetailCard
            styles={styles}
            userDetail={candidateDetail}
            setCandidateDetail={setCandidateDetail}
            isEditUserInfo
            onMergeCallback={getCandidateDetailById}
          />
          <Card
            className={styles.candidateInfoCard}
            bodyStyle={{ padding: "4px 0 1.5rem" }}
          >
            <Tabs
              defaultActiveKey={tab}
              onChange={setTab}
              className="candidates-info-card"
              tabBarGutter={40}
              items={[
                {
                  key: "jobs",
                  label: <span className="cic-tabs-label-style">Jobs</span>,
                  permission: K.Permissions.ViewCandidateJobs,
                  children: (
                    <div
                      className={`cic-tabs-content ${styles.cicTabsContent}`}
                    >
                      {candidateDetail?.candidateJobEntries?.map((cje) => (
                        <CandidateJobEntryCard
                          key={cje.id}
                          jobEntry={cje}
                          isActive={cje.id === candidateActivity?.id}
                          onClick={() => setJobEntry(cje.id)}
                          styles={styles}
                        />
                      ))}
                    </div>
                  ),
                },
                {
                  key: "activities",
                  label: <span className="cic-tabs-label-style">Activity</span>,
                  permission: K.Permissions.ViewCandidates,
                  children: (
                    <div
                      className={`cic-tabs-content ${styles.cicTabsContent}`}
                    >
                      <ActivityTab candidateId={candidateDetail.id} />
                    </div>
                  ),
                },
              ].filter(({ permission }) =>
                isPermissionPresent(permission, userSlice.roles),
              )}
            />
          </Card>
          <Modal
            className={`s2-theme-style ${styles1.ReasonForMoving}`}
            title="Reason for blacklist"
            open={isModalVisible}
            centered
            onCancel={handleCancelReasonModal}
            closeIcon={<i className={`icon-closeable ${styles1.closeAble}`} />}
            footer={[
              <Button key="1" onClick={handleCancelReasonModal}>
                Cancel
              </Button>,
              <Button key="2" type="primary" onClick={handleOkReasonModal}>
                Submit
              </Button>,
            ]}
          >
            <Form
              form={reasonForm}
              layout="vertical"
              className={styles1.ReasonForMovingFrom}
              onFinish={addToBlacklist}
            >
              <Form.Item name="id" initialValue={id} hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item
                label="Reason"
                name="reason"
                className={styles1.rfmFields}
              >
                <TextArea
                  showCount
                  maxLength={500}
                  placeholder="Reason"
                  className={`${styles1.rfmInput} ${styles1.rfmTextArea}`}
                  autoSize={{ minRows: 5, maxRows: 6 }}
                />
              </Form.Item>
            </Form>
          </Modal>
        </div>
        {candidateActivity && jobRecruiters && (
          <div
            className={`candidate-view-tabs ${styles.candidateViewActivity}`}
          >
            <ViewActivity
              styles={styles1}
              clientId={candidateActivity.jobSource.clientsId}
              jobDetails={{ ...candidateActivity.jobSource, jobRecruiters }}
              candidateActivity={mergeCandidateInJobEntry(
                candidateDetail,
                candidateActivity,
              )}
              setCandidateActivity={setCandidateActivity}
              onStatusChanged={updateCandidateJobEntry}
              defaultActiveTab={candidateTab}
              onTabChange={setCandidateTab}
              hideContactDetails
              onCancel={() => {
                setCandidateActivity(null);
                setParams({
                  jobEntry: null,
                  candidateTab: null,
                });
              }}
            />
          </div>
        )}
      </>
    )
  );
}
